import {
  Entry,
  EntryCollection,
  EntrySkeletonType,
  LocaleOption,
  MetadataTagsQueries,
} from 'contentful'
import type { BoxProps, HeadingProps } from '@alto-avios/alto-ui'
import { Iata } from '@reward-platform/platform-config'
import { isNotEmpty } from '@reward-platform/tsutil'
import { z } from 'zod'
import {
  ContentfulEntriesRequestParams,
  ContentfulLocale,
  ContentfulLocaleSchema,
  ContentfulMessageCluster,
  ContentfulOpCoTag,
  ContentfulMarketTag,
  Alignment,
} from './schemas'

export const getTextAlignment = (alignment?: Alignment): HeadingProps['textAlign'] => {
  switch (alignment) {
    case 'left':
      return 'start'
    case 'center':
      return 'center'
    case 'right':
      return 'end'
    default:
      return 'start'
  }
}

export const getContentAlignment = (alignment?: Alignment): BoxProps['alignItems'] => {
  switch (alignment) {
    case 'left':
      return 'flexStart'
    case 'center':
      return 'center'
    case 'right':
      return 'flexEnd'
    default:
      return 'center'
  }
}

const getContentfulOpCoTag = (opCo: Iata): ContentfulOpCoTag => {
  switch (opCo) {
    case 'BA':
      return 'british_airways'
    case 'EI':
      return 'aer_lingus'
    case 'IB':
      return 'iberia'
    default:
      return 'british_airways'
  }
}

const getContentfulMarketTag = (market: string): ContentfulMarketTag => {
  switch (market) {
    case 'GB':
      return 'market_gb'
    case 'US':
      return 'market_us'
    case 'IE':
      return 'market_ie'
    case 'ES':
      return 'market_es'
    default:
      return 'market_gb'
  }
}

export const validateContentfulLocale = (locale?: string): ContentfulLocale => {
  const localeParse = ContentfulLocaleSchema.safeParse(locale)
  if (localeParse.success) {
    return localeParse.data
  }

  return 'en-US'
}

// Helper functions for handling data from contentful rest response
export const getContentfulRequestModifiers = ({
  opCo,
  locale,
  market,
  application,
}: Partial<ContentfulEntriesRequestParams> = {}): MetadataTagsQueries & LocaleOption => {
  const opCoTag = opCo ? getContentfulOpCoTag(opCo) : null
  const marketTag = market ? getContentfulMarketTag(market) : null
  const optionalTags = [opCoTag, marketTag]
  const applicationTag = application ?? null

  return {
    'metadata.tags[exists]': true,
    'metadata.tags.sys.id[all]': [applicationTag, ...optionalTags].filter(isNotEmpty),
    locale: validateContentfulLocale(locale),
  }
}

export const getSingleItemFromCollection = <E extends EntrySkeletonType>(
  collection: EntryCollection<E, undefined, string>
) => {
  if (collection.items.length !== 1) {
    throw new Error(
      `Single item for content model not found, entries found: ${collection.items.length}`
    )
  }

  return collection.items[0]
}

export const mapEntryFieldsFromContentfulRestResponse = <
  E extends Entry,
  T extends z.ZodType = z.ZodUnknown
>(
  entry: E | E[],
  schema?: T
): z.output<T> => {
  const result = JSON.parse(
    JSON.stringify(entry, (_key, value) => {
      if (value?.fields) {
        const contentType = value.sys?.contentType ? value.sys.contentType.sys.id : undefined
        const entryId = value.sys?.id
        const revisionId = value.sys?.revision
        return { ...value.fields, contentType, entryId, revisionId }
      }
      if (value?.sys?.linkType === 'Entry') {
        // linked entry has no fields means it is not published
        return undefined
      }

      return value
    })
  )

  return schema ? schema.parse(result) : result
}

/** used to async parse the schema
 *  useful as the RichText schema is async for transforming markdown */
export const mapEntryFieldsFromContentfulRestResponseAsync = async <
  E extends Entry,
  T extends z.ZodType
>(
  entry: E | E[],
  schema: T
): Promise<z.output<T>> => {
  const maybeEntryFields = mapEntryFieldsFromContentfulRestResponse(entry)

  const safeParse = await schema.safeParseAsync(maybeEntryFields)
  if (!safeParse.success) {
    throw safeParse.error
  }
  return safeParse.data
}

interface MessageCluster {
  [key: string]: string | MessageCluster
}
interface MessageClusterRecord {
  [key: string]: MessageCluster
}
/**
 * Message Clusters
 * Formats multiple Message Clusters in the response from Contentful Collection request into a simple Clusters object containing flattened Messages objects
 */
export const transformMessageClustersResponse = (
  clustersResponse: ContentfulMessageCluster[]
): MessageClusterRecord => {
  const clusters: MessageClusterRecord = {}

  clustersResponse.forEach((cluster) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clusterResult: Record<string, any> = {}

    cluster.messages.forEach((item) => {
      const keys = item.key.split('.') // Split the key into parts
      let current = clusterResult

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          // If it's the last key, assign the value
          current[key] = item.value ?? item.defaultValue
        } else {
          // Otherwise, ensure the current level is an object
          current[key] ??= {} // sonar nullish coalescing operator
          current = current[key] ?? {}
        }
      })
    })

    clusters[cluster.key] = clusterResult
  })

  return clusters
}
